/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2021 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
export default class Murmur3 {
    /**
     * A MurmurHash3 implementation.
     *
     * @constructor
     */
    /**
     * @const
     * @type {number}
     */
    public static C1 = 0xcc9e2d51;

    /**
     * @const
     * @type {number}
     */
    public static C2 = 0x1b873593;

    /**
     * @const
     * @type {number}
     */
    public static R1 = 15;

    /**
     * @const
     * @type {number}
     */
    public static R2 = 13;

    /**
     * @const
     * @type {number}
     */
    public static M = 5;

    /**
     * @const
     * @type {number}
     */
    public static N = 0xe6546b64;

    /**
     * @param {number} m
     * @param {number} n
     *
     * @return {number}
     */
    public mult(m: number, n: number) {
        return (m & 0xffff) * n + ((((m >>> 16) * n) & 0xffff) << 16);
    }

    /**
     * Rotate left.
     *
     * @param {number} m The number to rotate
     * @param {number} n The number of positions to rotate left
     *
     * @return {number}
     */
    public rotl(m: number, n: number) {
        return (m << n) | (m >>> (32 - n));
    }

    /**
     * Calculate a murmur3 hash given a number and seed.
     *
     * @param {number} value
     * @param {number=} seed
     *
     * @return {number}
     */
    public hashNumber(value: number, seed: number) {
        let hash = seed || 0;

        let k = value & 0xffffffff;
        k = this.mult(k, Murmur3.C1);
        k = this.rotl(k, Murmur3.R1);
        k = this.mult(k, Murmur3.C2);

        hash ^= k;
        hash = this.rotl(hash, Murmur3.R2);
        hash = this.mult(hash, Murmur3.M) + Murmur3.N;

        hash ^= 4;
        hash ^= hash >>> 16;
        hash = this.mult(hash, 0x85ebca6b);
        hash ^= hash >>> 13;
        hash = this.mult(hash, 0xc2b2ae35);
        hash ^= hash >>> 16;

        return hash >>> 0;
    }

    /**
     * Calculate a murmur3 hash given a string and seed.
     *
     * @param {string} value
     * @param {number=} seed
     *
     * @return {number}
     */
    public hashString(value: string, seed: number) {
        let hash = seed || 0;
        let key = 0;
        let i = 0;

        const remainder = value.length % 4;
        const bytes = value.length - remainder;

        for (i = 0; i < bytes; i += 4) {
            key =
                ((value.charCodeAt(i + 0) & 0xffffffff) << 0) |
                ((value.charCodeAt(i + 1) & 0xffffffff) << 8) |
                ((value.charCodeAt(i + 2) & 0xffffffff) << 16) |
                ((value.charCodeAt(i + 3) & 0xffffffff) << 24);

            key = this.mult(key, Murmur3.C1);
            key = this.rotl(key, Murmur3.R1);
            key = this.mult(key, Murmur3.C2);

            hash ^= key;
            hash = this.rotl(hash, Murmur3.R2);
            hash = this.mult(hash, Murmur3.M) + Murmur3.N;
        }

        key = 0;

        switch (remainder) {
            case 3:
                key ^= (value.charCodeAt(i + 2) & 0xffffffff) << 16;
            case 2:
                key ^= (value.charCodeAt(i + 1) & 0xffffffff) << 8;
            case 1:
                key ^= (value.charCodeAt(i + 0) & 0xffffffff) << 0;
                key = this.mult(key, Murmur3.C1);
                key = this.rotl(key, Murmur3.R1);
                key = this.mult(key, Murmur3.C2);
                hash ^= key;
        }

        hash ^= value.length;
        hash ^= hash >>> 16;
        hash = this.mult(hash, 0x85ebca6b);
        hash ^= hash >>> 13;
        hash = this.mult(hash, 0xc2b2ae35);
        hash ^= hash >>> 16;

        return hash >>> 0;
    }
}
